import { useMutation, useQuery, useQueryClient } from 'react-query'
import { apiService } from '@/services/apiService'
import { useAuthStore } from '@/stores/authStore'

/**
 * Live member directory feed. Used by the Directory page to overlay
 * "currently at the lake" indicators on the static cottage data, and
 * by the Profile page to show the user's own residence state.
 */
export function useDirectory() {
  return useQuery({
    queryKey: ['directory'],
    queryFn: () => apiService.getDirectory(),
    // Presence is the kind of thing where 1-minute staleness is fine —
    // refetch on focus would be jarring.
    staleTime: 60 * 1000,
  })
}

/**
 * Toggle "I'm at the lake". Optimistic — flip the auth store immediately,
 * roll back on error.
 */
export function useSetInResidence() {
  const qc = useQueryClient()
  const updateUser = useAuthStore((s) => s.updateUser)

  return useMutation({
    mutationFn: ({ active, days }: { active: boolean; days?: number }) =>
      apiService.setInResidence(active, days),
    onSuccess: (updated) => {
      updateUser({ inResidenceUntil: (updated as any)?.inResidenceUntil ?? null })
      qc.invalidateQueries({ queryKey: ['directory'] })
    },
  })
}

/** Convenience: is this user currently in residence? */
export function isInResidence(inResidenceUntil: string | null | undefined): boolean {
  if (!inResidenceUntil) return false
  return new Date(inResidenceUntil).getTime() > Date.now()
}
